import { unitBelongsToCompany } from "./company";
import type { CompanyFilter, OrgChartViewConfig, OrgState, OrgUnit } from "../types/org";

export const DEFAULT_VIEW_CONFIG: OrgChartViewConfig = {
  rootUnitId: "",
  visibleMGrades: [],
  visibleMGradesByRootId: {},
  selectedUnitIds: [],
  selectedUnitIdsByRootId: {},
  hiddenUnitIds: [],
  hiddenUnitIdsByRootId: {},
  autoIncludeAncestors: true,
  hideDescendantsOfHiddenUnits: true,
  viewMode: "full",
};

export interface DisplayScopeOptions {
  company?: CompanyFilter;
  includeUnlinked?: boolean;
}

export function normalizeViewConfig(config?: Partial<OrgChartViewConfig>): OrgChartViewConfig {
  return {
    rootUnitId: config?.rootUnitId ?? DEFAULT_VIEW_CONFIG.rootUnitId,
    visibleMGrades: unique((config?.visibleMGrades ?? []).map(normalizeMGrade).filter(Boolean)).sort(compareMGradeDesc),
    visibleMGradesByRootId: normalizeVisibleMGradesByRootId(config?.visibleMGradesByRootId),
    selectedUnitIds: unique(config?.selectedUnitIds ?? []),
    selectedUnitIdsByRootId: normalizeUnitIdsByRootId(config?.selectedUnitIdsByRootId),
    hiddenUnitIds: unique(config?.hiddenUnitIds ?? []),
    hiddenUnitIdsByRootId: normalizeUnitIdsByRootId(config?.hiddenUnitIdsByRootId),
    autoIncludeAncestors: config?.autoIncludeAncestors ?? DEFAULT_VIEW_CONFIG.autoIncludeAncestors,
    hideDescendantsOfHiddenUnits: config?.hideDescendantsOfHiddenUnits ?? DEFAULT_VIEW_CONFIG.hideDescendantsOfHiddenUnits,
    viewMode: config?.viewMode === "custom" ? "custom" : "full",
  };
}

function normalizeUnitIdsByRootId(value?: Record<string, string[]>): Record<string, string[]> {
  if (!value) return {};
  return Object.fromEntries(Object.entries(value)
    .map(([rootUnitId, ids]) => [rootUnitId, unique(ids ?? [])])
    .filter(([rootUnitId, ids]) => Boolean(rootUnitId && (ids as string[]).length)));
}

function normalizeVisibleMGradesByRootId(value?: Record<string, string[]>): Record<string, string[]> {
  if (!value) return {};
  return Object.fromEntries(Object.entries(value)
    .map(([rootUnitId, grades]) => [
      rootUnitId,
      unique((grades ?? []).map(normalizeMGrade).filter(Boolean)).sort(compareMGradeDesc),
    ])
    .filter(([rootUnitId, grades]) => Boolean(rootUnitId && (grades as string[]).length)));
}

export function ensureViewConfig(state: OrgState): OrgState {
  state.viewConfig = normalizeViewConfig(state.viewConfig);
  return state;
}

export function visibleUnitIdsForViewConfig(state: OrgState, config = normalizeViewConfig(state.viewConfig)): string[] {
  if (config.viewMode !== "custom") return [];

  const root = state.units[config.rootUnitId];
  const company = state.companyFilter ?? "Qisda";
  if (!root || root.isUnlinked || !unitBelongsToCompany(state, root, company)) return [];

  const scopeUnits = Object.values(state.units).filter((unit) => !unit.isUnlinked && unitBelongsToCompany(state, unit, company));
  const subtreeIds = collectUnitSubtree(root.id, scopeUnits);
  const subtreeSet = new Set(subtreeIds);
  const visibleGradeSet = new Set(config.visibleMGrades.map(normalizeMGrade).filter(Boolean));
  const hiddenSet = hiddenUnitSet(state, config, subtreeSet);
  hiddenSet.delete(root.id);

  const isGradeVisible = (unit: OrgUnit) => {
    if (unit.id === root.id) return true;
    const grade = normalizeMGrade(unit.mGrade);
    if (!grade) return isUngradedUnitVisibleInScope(root, unit);
    if (!visibleGradeSet.size) return false;
    return Boolean(grade && visibleGradeSet.has(grade));
  };

  const selectedCandidates = config.selectedUnitIds.filter((id) => {
    const unit = state.units[id];
    return Boolean(unit && subtreeSet.has(id) && !hiddenSet.has(id) && isGradeVisible(unit));
  });

  const visible = new Set<string>([root.id]);
  const seedIds = selectedCandidates.length
    ? selectedCandidates
    : subtreeIds.filter((id) => {
      const unit = state.units[id];
      return Boolean(unit && !hiddenSet.has(id) && isGradeVisible(unit));
    });

  for (const id of seedIds) {
    visible.add(id);
    addAncestorsToRoot(state, id, root.id, visible, subtreeSet);
  }

  for (const id of [...visible]) {
    if (id !== root.id && hiddenSet.has(id)) visible.delete(id);
  }

  closeVisibleParents(state, root.id, visible, subtreeSet, hiddenSet);
  return subtreeIds.filter((id) => visible.has(id));
}

export function displayScopeRootOptions(state: OrgState, options: DisplayScopeOptions = {}) {
  const company = options.company ?? state.companyFilter ?? "Qisda";
  return Object.values(state.units)
    .filter((unit) => (options.includeUnlinked || !unit.isUnlinked) && unitBelongsToCompany(state, unit, company))
    .sort(compareUnit);
}

export function resolveDisplayScopeRoot(state: OrgState, config = normalizeViewConfig(state.viewConfig)) {
  const company = state.companyFilter ?? "Qisda";
  const configuredRoot = state.units[config.rootUnitId];
  if (configuredRoot && !configuredRoot.isUnlinked && unitBelongsToCompany(state, configuredRoot, company)) return configuredRoot;
  return displayScopeRootOptions(state, { company })[0];
}

export function mGradesInSubtree(state: OrgState, rootUnitId: string) {
  const root = state.units[rootUnitId];
  const company = state.companyFilter ?? "Qisda";
  if (!root || root.isUnlinked || !unitBelongsToCompany(state, root, company)) return [];
  const scopeUnits = Object.values(state.units).filter((unit) => !unit.isUnlinked && unitBelongsToCompany(state, unit, company));
  const subtreeIds = collectUnitSubtree(root.id, scopeUnits);
  return unique(subtreeIds.map((id) => normalizeMGrade(state.units[id]?.mGrade)).filter(Boolean)).sort(compareMGradeDesc);
}

export function defaultVisibleMGradesForRoot(state: OrgState, rootUnitId: string) {
  const grades = mGradesInSubtree(state, rootUnitId);
  const root = state.units[rootUnitId];
  if (!root || !isManagementScopeRoot(state, root)) return grades;
  return grades.filter((grade) => {
    const value = mGradeNumber(grade);
    return value >= 8 && value <= 12;
  });
}

export function selectableUnitIdsForScope(state: OrgState, config = normalizeViewConfig(state.viewConfig)) {
  const root = state.units[config.rootUnitId];
  const company = state.companyFilter ?? "Qisda";
  if (!root || root.isUnlinked || !unitBelongsToCompany(state, root, company)) return [];
  const grades = new Set(config.visibleMGrades.map(normalizeMGrade).filter(Boolean));
  const scopeUnits = Object.values(state.units).filter((unit) => !unit.isUnlinked && unitBelongsToCompany(state, unit, company));
  return collectUnitSubtree(root.id, scopeUnits).filter((id) => {
    if (id === root.id) return true;
    const grade = normalizeMGrade(state.units[id]?.mGrade);
    if (!grade) return isUngradedUnitVisibleInScope(root, state.units[id]);
    if (!grades.size) return false;
    return grades.has(grade);
  });
}

export function collectUnitSubtree(rootUnitId: string, units: OrgUnit[]) {
  const children = childrenMapFor(units);
  const ids: string[] = [];
  const walk = (unitId: string) => {
    ids.push(unitId);
    for (const child of [...(children.get(unitId) ?? [])].sort(compareUnit)) walk(child.id);
  };
  walk(rootUnitId);
  return ids;
}

export function normalizeMGrade(value?: string) {
  const match = String(value ?? "").match(/M*\s*(\d+)/i);
  return match ? `M${match[1]}` : "";
}

export function compareMGradeDesc(a: string, b: string) {
  return Number(b.slice(1)) - Number(a.slice(1));
}

function isManagementScopeRoot(state: OrgState, unit: OrgUnit) {
  const code = unit.code.trim().toUpperCase();
  if (unitBelongsToCompany(state, unit, "BQP")) return code === "P00";
  if (unitBelongsToCompany(state, unit, "BenQ")) return code === "B000" || code === "D000";
  if (unitBelongsToCompany(state, unit, "Qisda")) return code === "E000" || code === "C000";
  return false;
}

function isUngradedUnitVisibleInScope(root: OrgUnit, unit?: OrgUnit) {
  return Boolean(unit && root.code.trim().toUpperCase() === "DB00");
}

function mGradeNumber(value?: string) {
  const match = String(value ?? "").match(/^M(\d+)$/i);
  return match ? Number(match[1]) : 0;
}

function hiddenUnitSet(state: OrgState, config: OrgChartViewConfig, subtreeSet: Set<string>) {
  const hidden = new Set(config.hiddenUnitIds.filter((id) => subtreeSet.has(id)));
  if (!config.hideDescendantsOfHiddenUnits) return hidden;
  const units = Object.values(state.units).filter((unit) => !unit.isUnlinked && subtreeSet.has(unit.id));
  for (const id of [...hidden]) {
    for (const descendantId of collectUnitSubtree(id, units)) hidden.add(descendantId);
  }
  return hidden;
}

function addAncestorsToRoot(state: OrgState, unitId: string, rootUnitId: string, visible: Set<string>, subtreeSet: Set<string>) {
  let current: OrgUnit | undefined = state.units[unitId];
  while (current && subtreeSet.has(current.id)) {
    visible.add(current.id);
    if (current.id === rootUnitId) return;
    current = current.parentId ? state.units[current.parentId] : undefined;
  }
}

function closeVisibleParents(state: OrgState, rootUnitId: string, visible: Set<string>, subtreeSet: Set<string>, hiddenSet: Set<string>) {
  let changed = true;
  while (changed) {
    changed = false;
    for (const id of [...visible]) {
      if (id === rootUnitId) continue;
      const parentId = state.units[id]?.parentId;
      if (!parentId || !subtreeSet.has(parentId) || hiddenSet.has(parentId) || visible.has(parentId)) continue;
      visible.add(parentId);
      changed = true;
    }
  }
}

function childrenMapFor(units: OrgUnit[]) {
  const children = new Map<string, OrgUnit[]>();
  for (const unit of units) {
    if (!unit.parentId) continue;
    children.set(unit.parentId, [...(children.get(unit.parentId) ?? []), unit]);
  }
  return children;
}

function compareUnit(a: OrgUnit, b: OrgUnit) {
  return a.code.localeCompare(b.code, undefined, { numeric: true }) || a.level - b.level || a.nameZh.localeCompare(b.nameZh);
}

function unique<T>(values: T[]) {
  return [...new Set(values)];
}
